import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { openPdf } from "../../lib/openPdf";
import { useAuth } from "../../lib/auth";
import {
  Buildings, IdentificationCard, FileArrowUp, PencilSimple,
  CheckCircle, X, ArrowClockwise,
} from "@phosphor-icons/react";

// M3 Slice 5 — permanent Sender Company Details area. View immutable PAN + recorded GSTINs, edit the
// company legal name / address (prospective, last-write-wins), and append a GSTIN from an official
// GST Registration Certificate. Any active Sender member of the company may edit and add GSTINs.
const ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

function addrText(a) {
  return typeof a === "string" ? a : a ? JSON.stringify(a) : "";
}

export default function CompanyDetails() {
  const { profile } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [reviewingGst, setReviewingGst] = useState(false);
  const [confirmingGst, setConfirmingGst] = useState(false);
  const [gstReview, setGstReview] = useState(null); // client-side pending review {review_id, gstin_display, pan_matches}
  const gstInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data: d } = await api.get("/company");
      setData(d);
      setName(d.legal_name || "");
      setAddress(addrText(d.registered_address));
    } catch (e) {
      setLoadError(e.displayMessage || "We couldn’t load your company details.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (profile && profile.role !== "SENDER") return <Navigate to="/app" replace />;

  const startEdit = () => {
    setName(data.legal_name || "");
    setAddress(addrText(data.registered_address));
    setSaveError("");
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setSaveError(""); };

  const save = async () => {
    if (!name.trim() || !address.trim()) {
      setSaveError("Company name and address are required.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const { data: d } = await api.patch("/company", {
        legal_name: name.trim(),
        registered_address: address.trim(),
      });
      setData(d);
      setEditing(false);
      toast.success("Company details updated.");
    } catch (e) {
      setSaveError(e.displayMessage || "We couldn’t save your changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Review-before-acceptance: upload reads and validates the GSTIN ONCE and stores a short-lived
  // server review record; confirm appends the exact reviewed value by review_id (no second read,
  // no client-supplied identifier). GSTIN is never manually editable.
  const reviewGstin = async (file) => {
    if (!file) return;
    setReviewingGst(true);
    setGstReview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: d } = await api.post("/company/gstins/review", fd);
      setGstReview({ review_id: d.review_id, gstin_display: d.gstin_display, pan_matches: d.pan_matches });
    } catch (e) {
      toast.error(e.displayMessage || "We couldn’t process this document right now. Please try again.");
    } finally {
      setReviewingGst(false);
      if (gstInput.current) gstInput.current.value = "";
    }
  };

  const confirmAddGstin = async () => {
    if (!gstReview) return;
    setConfirmingGst(true);
    try {
      const { data: d } = await api.post("/company/gstins", { review_id: gstReview.review_id });
      setData({
        pan_masked: d.pan_masked,
        legal_name: d.legal_name,
        registered_address: d.registered_address,
        gstins: d.gstins,
      });
      setGstReview(null);
      toast.success(d.message || "GSTIN added.");
    } catch (e) {
      toast.error(e.displayMessage || "We couldn’t process this document right now. Please try again.");
    } finally {
      setConfirmingGst(false);
    }
  };

  const cancelGstReview = async () => {
    setGstReview(null);
    try { await api.delete("/company/gstins/review"); } catch (e) { /* best-effort */ }
  };

  if (loading) {
    return (
      <div className="p-10 mono text-sm" data-testid="company-loading">Loading…</div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-[900px] mx-auto p-6" data-testid="company-load-error">
        <div className="grid-card p-8 text-center">
          <div className="overline mb-1">COMPANY DETAILS</div>
          <p className="text-sm text-[hsl(var(--muted))] mt-2">{loadError}</p>
          <button className="btn-primary justify-center mt-5 mx-auto" onClick={load} data-testid="company-retry-button">
            <ArrowClockwise size={14} weight="bold" /> Retry
          </button>
        </div>
      </div>
    );
  }

  const gstins = data?.gstins || [];

  return (
    <div className="max-w-[900px] mx-auto p-6" data-testid="company-details-page">
      <div className="mb-6">
        <div className="overline">SENDER PORTAL</div>
        <h1 className="heading text-3xl flex items-center gap-2">
          <Buildings size={26} weight="bold" /> Company details
        </h1>
      </div>

      {/* Identity + editable details */}
      <div className="grid-card grid-card-strong p-6 mb-6" data-testid="company-identity-card">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <IdentificationCard size={18} weight="bold" className="text-[hsl(var(--accent))]" />
            <span className="overline">COMPANY IDENTITY</span>
          </div>
          {!editing && (
            <button className="btn-ghost" onClick={startEdit} data-testid="company-edit-button">
              <PencilSimple size={14} weight="bold" /> Edit
            </button>
          )}
        </div>

        <div className="mb-5">
          <div className="overline text-[10px] mb-1">Company PAN</div>
          <div className="font-mono text-lg tracking-wide" data-testid="company-pan">{data.pan_masked || "—"}</div>
          <div className="text-[11px] text-[hsl(var(--muted))] mt-1">PAN is permanent and cannot be changed.</div>
        </div>

        {data.agreement?.accepted && (
          <div className="mb-5" data-testid="company-agreement-block">
            <div className="overline text-[10px] mb-1">Air12 Sender Agreement</div>
            <button className="btn-ghost text-sm" data-testid="company-agreement-download"
                    onClick={() => openPdf("/company/agreement/document").catch(() => toast.error("Could not open the document. Please try again shortly."))}>
              Download Air12 Sender Agreement (v{data.agreement.version} · accepted {new Date(data.agreement.accepted_at).toLocaleDateString()})
            </button>
          </div>
        )}

        {editing ? (
          <div className="space-y-3" data-testid="company-edit-form">
            <label className="block">
              <div className="overline mb-1.5">Company legal name</div>
              <input className="input-field" value={name} onChange={(e) => setName(e.target.value)} data-testid="company-name-input" />
            </label>
            <label className="block">
              <div className="overline mb-1.5">Company address</div>
              <textarea className="input-field min-h-[90px]" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="company-address-input" />
            </label>
            {saveError && (
              <div className="text-sm text-red-600" data-testid="company-validation-error">{saveError}</div>
            )}
            <div className="flex gap-2">
              <button className="btn-ghost" disabled={saving} onClick={cancelEdit} data-testid="company-cancel-button">
                <X size={14} weight="bold" /> Cancel
              </button>
              <button className="btn-primary ml-auto" disabled={saving} onClick={save} data-testid="company-save-button">
                <CheckCircle size={14} weight="bold" /> {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="overline text-[10px] mb-1">Company legal name</div>
              <div className="text-sm font-semibold" data-testid="company-legal-name">{data.legal_name || "—"}</div>
            </div>
            <div>
              <div className="overline text-[10px] mb-1">Company address</div>
              <div className="text-sm whitespace-pre-line" data-testid="company-address">{addrText(data.registered_address) || "—"}</div>
            </div>
          </div>
        )}
      </div>

      {/* GST registrations */}
      <div className="grid-card grid-card-strong p-6" data-testid="company-gstins-card">
        <div className="flex items-center justify-between mb-4">
          <span className="overline">GST registrations</span>
          {!gstReview && (
            <button className="btn-ghost" disabled={reviewingGst} onClick={() => gstInput.current?.click()} data-testid="add-gstin-button">
              <FileArrowUp size={14} weight="bold" /> {reviewingGst ? "Processing…" : "Add GSTIN"}
            </button>
          )}
        </div>

        {reviewingGst && (
          <div className="text-sm text-[hsl(var(--muted))] mb-3" data-testid="gstin-processing">Reading your GST Registration Certificate…</div>
        )}

        {gstReview && (
          <div className="border rounded-[2px] p-4 mb-4" data-testid="gstin-review">
            <div className="overline text-[10px]">EXTRACTED GSTIN · REVIEW (READ-ONLY)</div>
            <div className="font-mono text-lg tracking-wide" data-testid="gstin-review-value">{gstReview.gstin_display}</div>
            {gstReview.pan_matches && (
              <div className="text-xs text-emerald-700 flex items-center gap-1 mt-2" data-testid="gstin-pan-match">
                <CheckCircle size={13} weight="fill" /> Certificate PAN matches your company PAN.
              </div>
            )}
            <p className="text-[11px] text-[hsl(var(--muted))] mt-3">If this is wrong, choose Replace document. GSTIN cannot be edited by hand.</p>
            <div className="flex gap-2 mt-3 flex-wrap">
              <button className="btn-ghost" disabled={confirmingGst} onClick={() => gstInput.current?.click()} data-testid="gstin-replace-button">
                <FileArrowUp size={13} weight="bold" /> Replace document
              </button>
              <button className="btn-ghost" disabled={confirmingGst} onClick={cancelGstReview} data-testid="gstin-cancel-button">
                <X size={13} weight="bold" /> Cancel
              </button>
              <button className="btn-primary ml-auto" disabled={confirmingGst} onClick={confirmAddGstin} data-testid="gstin-confirm-add-button">
                <CheckCircle size={14} weight="bold" /> {confirmingGst ? "Adding…" : "Add GSTIN"}
              </button>
            </div>
          </div>
        )}

        {gstins.length === 0 ? (
          <div className="text-sm text-[hsl(var(--muted))] py-4 text-center" data-testid="gstin-empty">
            No GSTIN recorded yet. Add one using your official GST Registration Certificate.
          </div>
        ) : (
          <ul className="divide-y" data-testid="gstin-list">
            {gstins.map((g, i) => {
              const inactive = (g.status || "ACTIVE") !== "ACTIVE";
              return (
              <li key={g.id || i} className="py-2.5 flex items-center gap-2" data-testid={`gstin-item-${i}`}>
                <CheckCircle size={16} weight="fill" className={inactive ? "text-[hsl(var(--muted))]" : "text-[hsl(var(--accent))]"} />
                <span className={`font-mono text-sm tracking-wide ${inactive ? "line-through text-[hsl(var(--muted))]" : ""}`}>{g.gstin_masked}</span>
                {inactive && (
                  <span className="ml-auto text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-sm bg-[hsl(var(--muted))]/15 text-[hsl(var(--muted))]" data-testid={`gstin-inactive-badge-${i}`}>Inactive</span>
                )}
              </li>
              );
            })}
          </ul>
        )}

        <p className="text-[11px] text-[hsl(var(--muted))] mt-3">
          GSTINs are permanent and cannot be edited or removed. The certificate PAN must match your company PAN. PDF, JPG or PNG up to 5 MB.
        </p>

        <input ref={gstInput} type="file" accept={ACCEPT} className="hidden"
               onChange={(e) => reviewGstin(e.target.files?.[0])} data-testid="gstin-file-input" />
      </div>
    </div>
  );
}
